// src/components/ui/IconPicker/IconPickerModal.tsx
import React, { useState, useEffect } from 'react';
import { IonIcon } from '@ionic/react';
import { sparklesOutline } from 'ionicons/icons';
import { Modal, ModalSection } from '../Modal';
import { IconPicker, IconDisplay, type IconValue, type IconType } from './IconPicker';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export interface IconPickerModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Save handler - called with the new icon value */
  onSave: (icon: IconValue) => void | Promise<void>;
  /** Current icon value */
  currentIcon?: IconValue | null;
  /** Title for the modal */
  title?: string;
  /** Subtitle/description */
  subtitle?: string;
  /** Default tab to show */
  defaultTab?: IconType;
  /** Limit which tabs are shown */
  tabs?: IconType[];
  /** Whether save is in progress */
  saving?: boolean;
  /** Callback for file uploads - can be used for server upload */
  onFileUpload?: (file: File) => Promise<string>;
  /** Maximum file size in bytes for uploads (default: 2MB) */
  maxFileSize?: number;
}

// ═══════════════════════════════════════════════════════════════════
// ICON PICKER MODAL COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const IconPickerModal: React.FC<IconPickerModalProps> = ({
  isOpen,
  onClose,
  onSave,
  currentIcon,
  title = 'Choose Icon',
  subtitle,
  defaultTab = 'emoji',
  tabs = ['emoji', 'ionic', 'mdi', 'lucide', 'upload', 'url'],
  saving = false,
  onFileUpload,
  maxFileSize,
}) => {
  const [selectedIcon, setSelectedIcon] = useState<IconValue | null>(currentIcon || null);
  const [isSaving, setIsSaving] = useState(false);

  // Reset selected icon when modal opens with new current value
  useEffect(() => {
    if (isOpen) {
      setSelectedIcon(currentIcon || null);
    }
  }, [isOpen, currentIcon]);

  // Handle save
  const handleSave = async () => {
    if (!selectedIcon) return;

    setIsSaving(true);
    try {
      await onSave(selectedIcon);
      onClose();
    } catch (err) {
      console.error('Failed to save icon:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Check if selection has changed
  const hasChanged = JSON.stringify(selectedIcon) !== JSON.stringify(currentIcon);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      header={{
        title,
        subtitle,
      }}
      footer={{
        primaryAction: {
          label: isSaving || saving ? 'Saving...' : 'Select',
          onClick: handleSave,
          disabled: !selectedIcon || !hasChanged || isSaving || saving,
        },
        secondaryAction: {
          label: 'Cancel',
          onClick: onClose,
        },
      }}
    >
      <ModalSection spacing="normal">
        {/* Current Icon Preview */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: 24,
          paddingBottom: 24,
          borderBottom: '1px solid var(--ion-border-color)',
        }}>
          <div style={{
            width: 80,
            height: 80,
            borderRadius: 16,
            backgroundColor: 'var(--ion-color-step-50)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid var(--ion-border-color)',
            marginBottom: 12,
          }}>
            {selectedIcon ? (
              <IconDisplay icon={selectedIcon} size={48} />
            ) : (
              <IonIcon
                icon={sparklesOutline}
                style={{ fontSize: 36, color: 'var(--ion-color-medium)' }}
              />
            )}
          </div>

          <div style={{
            fontFamily: 'var(--kff-data-font)',
            fontSize: 14,
            color: 'var(--ion-color-medium)',
            textAlign: 'center',
          }}>
            {selectedIcon ? (
              hasChanged ? 'New icon selected' : 'Current icon'
            ) : (
              'Select an icon below'
            )}
          </div>
        </div>

        {/* Icon Picker */}
        <IconPicker
          value={selectedIcon}
          onChange={setSelectedIcon}
          defaultTab={defaultTab}
          tabs={tabs}
          iconSize={28}
          maxHeight={280}
          onFileUpload={onFileUpload}
          maxFileSize={maxFileSize}
        />
      </ModalSection>
    </Modal>
  );
};

IconPickerModal.displayName = 'IconPickerModal';

// ═══════════════════════════════════════════════════════════════════
// CLICKABLE ICON COMPONENT
// ═══════════════════════════════════════════════════════════════════

export interface ClickableIconProps {
  /** Current icon value */
  icon?: IconValue | null;
  /** Fallback emoji if no icon is set */
  fallback?: string;
  /** Size of the icon container */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Click handler - opens the picker modal */
  onClick?: () => void;
  /** Background color */
  backgroundColor?: string;
  /** Whether to show edit overlay on hover */
  showEditOverlay?: boolean;
  /** Custom className */
  className?: string;
  /** Custom style */
  style?: React.CSSProperties;
}

const SIZE_MAP = {
  sm: { container: 40, icon: 24, overlay: 14 },
  md: { container: 56, icon: 32, overlay: 18 },
  lg: { container: 72, icon: 42, overlay: 22 },
  xl: { container: 96, icon: 56, overlay: 28 },
};

export const ClickableIcon: React.FC<ClickableIconProps> = ({
  icon,
  fallback = '📢',
  size = 'md',
  onClick,
  backgroundColor = 'var(--ion-color-step-50)',
  showEditOverlay = true,
  className,
  style,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const sizes = SIZE_MAP[size];

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={className}
      style={{
        position: 'relative',
        width: sizes.container,
        height: sizes.container,
        borderRadius: 12,
        backgroundColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid var(--ion-border-color)',
        padding: 0,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.15s ease',
        transform: isHovered && onClick ? 'scale(1.05)' : 'scale(1)',
        boxShadow: isHovered && onClick
          ? '0 4px 12px rgba(0, 0, 0, 0.15)'
          : '0 2px 6px rgba(0, 0, 0, 0.08)',
        flexShrink: 0,
        ...style,
      }}
      aria-label={onClick ? 'Change icon' : 'Icon'}
    >
      {/* Icon content */}
      <div style={{
        transition: 'opacity 0.15s ease',
        opacity: isHovered && showEditOverlay && onClick ? 0.5 : 1,
      }}>
        {icon ? (
          <IconDisplay icon={icon} size={sizes.icon} />
        ) : (
          <span style={{ fontSize: sizes.icon }}>{fallback}</span>
        )}
      </div>

      {/* Edit overlay */}
      {showEditOverlay && onClick && (
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.15s ease',
          borderRadius: 10,
        }}>
          <IonIcon
            icon={sparklesOutline}
            style={{
              fontSize: sizes.overlay,
              color: 'white',
            }}
          />
        </div>
      )}
    </button>
  );
};

ClickableIcon.displayName = 'ClickableIcon';

export default IconPickerModal;
